import React, { useState } from "react";
import axios from "axios";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICKETS = [
  "₹1 Cr – ₹2.5 Cr",
  "₹2.5 Cr – ₹5 Cr",
  "₹5 Cr – ₹10 Cr",
  "₹10 Cr+",
];

export default function InvestForm({ open, onClose }) {
  const [form, setForm] = useState({
    name: "", entity: "", ticket_size: TICKETS[0], email: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/invest`, form);
      setSuccess(true);
      toast.success("Thank you. Our IR team will be in touch.");
      setTimeout(() => {
        setSuccess(false);
        onClose();
        setForm({ name: "", entity: "", ticket_size: TICKETS[0], email: "", message: "" });
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Submission failed. Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="invest-form-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-[#0F2818]/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-xl bg-[#F7F8EF] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
      >
        <div className="px-7 py-5 border-b border-[#14401F]/10 flex items-center justify-between bg-[#14401F] text-white">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#B8D958] font-semibold">
              For Investors
            </div>
            <h3 className="font-serif-display text-2xl font-bold mt-1">Invest With Us</h3>
          </div>
          <button
            data-testid="invest-form-close"
            onClick={onClose}
            className="h-10 w-10 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {success ? (
          <div className="flex-1 flex flex-col items-center justify-center px-7 py-16 text-center">
            <CheckCircle2 className="h-14 w-14 text-[#14401F] mb-4" />
            <h4 className="font-serif-display text-2xl font-bold text-[#0F2818]">
              Thank you.
            </h4>
            <p className="mt-2 text-[#4A5A4F]">
              Our IR team will reach out shortly.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="flex-1 overflow-y-auto px-7 py-6 space-y-4">
            <Field label="Your Name" testid="invest-name" value={form.name} onChange={(v) => set("name", v)} required />
            <Field label="Entity / Family Office" testid="invest-entity" value={form.entity} onChange={(v) => set("entity", v)} placeholder="Optional" />
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-[#14401F] font-semibold mb-2">
                Ticket Size Range
              </span>
              <select
                data-testid="invest-ticket"
                value={form.ticket_size}
                onChange={(e) => set("ticket_size", e.target.value)}
                className="w-full rounded-2xl border border-[#14401F]/15 bg-white px-4 py-3 text-sm text-[#0F2818] focus:border-[#14401F] focus:outline-none focus:ring-2 focus:ring-[#B8D958]/40"
              >
                {TICKETS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <Field label="Email" testid="invest-email" value={form.email} onChange={(v) => set("email", v)} type="email" required />
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-[#14401F] font-semibold mb-2">
                Message
              </span>
              <textarea
                data-testid="invest-message"
                rows={4}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
                placeholder="Tell us what brought you here."
                className="w-full rounded-2xl border border-[#14401F]/15 bg-white px-4 py-3 text-sm text-[#0F2818] placeholder:text-[#4A5A4F]/50 focus:border-[#14401F] focus:outline-none focus:ring-2 focus:ring-[#B8D958]/40 resize-none"
              />
            </label>

            <button
              type="submit"
              data-testid="invest-submit"
              disabled={submitting}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#14401F] text-[#B8D958] hover:bg-[#0F2818] disabled:opacity-60 font-semibold rounded-full px-7 py-4 transition-all"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Submitting…" : "Send Inquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[#14401F] font-semibold mb-2">
        {label} {required && <span className="text-[#E8A33D]">*</span>}
      </span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl border border-[#14401F]/15 bg-white px-4 py-3 text-sm text-[#0F2818] placeholder:text-[#4A5A4F]/50 focus:border-[#14401F] focus:outline-none focus:ring-2 focus:ring-[#B8D958]/40 transition-all"
      />
    </label>
  );
}
